import { useServices } from '@/shared/hooks/use-services';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { familyForItem, lowestTierPrice } from '@/shared/utils/wholesale-pricing';

import type { StoreItem, StoreItemsByCategory } from '../../types/get-store-items';
import { getDisplayPrice } from '../../utils/display-price';
import { resolveMenuCategories, withCombosCategory } from '../../utils/virtual-categories';

import { useStoreSlug } from '@/shared/hooks/use-store-slug';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useGetMerchantDetails } from '../../hooks/http/use-get-merchant-details';
import { useStoreStatus } from '../../hooks/use-store-status';
import { usePromotionalItems } from '../../hooks/use-promotional-items';
import { usePromotionsModalSession } from '../../hooks/use-promotions-modal-session';
import { usePendingCouponFromUrl } from '@/modules/orders/hooks/use-pending-coupon-from-url';
import { useReferralProgram, useStoreReferralCapture } from '@/modules/store-referral/hooks';
import { useAddresses } from '@/modules/location/hooks/use-addresses';
import { useNavigation } from '@/shared/contexts/navigation-context';
import { hasSeenThisSession, markSeenThisSession } from '@/shared/utils/session-flag';

import { usePageTracking } from '@/modules/analytics/hooks/use-page-tracking';
import { useTracking } from '@/modules/analytics/hooks/use-tracking';
import { useClubSummary } from '@/modules/club/hooks/http/use-club-summary';
import { getPendingCoupon, savePendingCoupon } from '@/modules/orders/utils/pending-coupon';
import { enhancedToast } from '@/shared/utils/enhanced-toast';
import { useStoreCoupons } from '../../services/store-coupons';
import type { StoreCoupon } from '../../types/store-coupon';
import { pickCouponProgress } from '../../utils/store-benefits';
import { pickHighlights } from '../../utils/store-highlights';
import { isCategoryInQuantityPromotion } from '../../utils/quantity-promotion-scope';
import {
  isItemInQuantityPromotion,
  isQuantityPromotionActive,
  quantityPromotionChipLabel,
  quantityPromotionLabel,
} from '@/shared/utils/quantity-promotion';
import { QUANTITY_PROMOTION_CHANNEL } from '@/modules/cart/hooks/use-cart-quantity-promotion';

export function useStoreModel() {
  const { slug } = useStoreSlug();
  const [location, navigate] = useLocation();
  const { getStoreItems, listItems } = useServices();
  const { address } = useAddresses();
  const { navigateBackward } = useNavigation();

  usePageTracking();
  const track = useTracking();

  // PRAT-19: visita da loja (store_view), uma por abertura da página.
  useEffect(() => {
    if (slug) track.storeView();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- uma vez por loja aberta
  }, [slug]);

  // Captura o cupom de deep-link (`/{slug}?cupom=CODIGO`) e o guarda como
  // pendente para aplicação posterior no fluxo de pedido (TASK-004).
  usePendingCouponFromUrl();

  // Indique & Ganhe (PRAT-12): captura o `?ref=` do link de indicação e vincula
  // a indicação assim que o cliente estiver logado.
  useStoreReferralCapture(slug);
  const referralProgramQuery = useReferralProgram(slug);

  const [isStoreDetailsModalOpen, setIsStoreDetailsModalOpen] = useState(false);
  const [isSchedulingInfoModalOpen, setIsSchedulingInfoModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [isProductDrawerOpen, setIsProductDrawerOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  // Detecta parâmetros de edição na URL e abre o drawer
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const editProductId = urlParams.get('editProduct');
    const editItemId = urlParams.get('editItemId');

    if (editProductId) {
      console.log('🔵 Store detectou edição:', {
        editProductId,
        editItemId,
        currentSelectedProductId: selectedProductId,
        isDrawerOpen: isProductDrawerOpen,
      });

      // Só atualiza se for diferente do atual para evitar loops
      if (selectedProductId !== editProductId) {
        setSelectedProductId(editProductId);
        setIsProductDrawerOpen(true);
      }

      // IMPORTANTE: NÃO limpa a URL aqui!
      // O drawer precisa dos parâmetros para funcionar corretamente
      // A URL será limpa quando o drawer for fechado
    }
  }, [location, slug, navigate, selectedProductId, isProductDrawerOpen]);

  // Use new merchant details API with coordinates
  const merchantDetailsQuery = useGetMerchantDetails({
    slug: slug!,
    latitude: address?.selected?.coordinates?.latitude
      ? String(address.selected.coordinates.latitude)
      : undefined,
    longitude: address?.selected?.coordinates?.longitude
      ? String(address.selected.coordinates.longitude)
      : undefined,
  });

  // Keep old query for backward compatibility during transition
  const storeInfoQuery = merchantDetailsQuery;

  // Status efetivo da loja: uma loja fechada que aceita agendamento continua
  // navegável para que o usuário monte o pedido e agende para mais tarde.
  const storeStatus = useStoreStatus(slug, storeInfoQuery.data);

  // Modal informativo exibido uma única vez por sessão, por loja, quando a
  // loja está fechada mas aceita pedidos agendados.
  useEffect(() => {
    if (!slug || !storeStatus.isClosedButSchedulable) return;

    const sessionKey = `store-scheduling-info:${slug}`;
    if (hasSeenThisSession(sessionKey)) return;

    setIsSchedulingInfoModalOpen(true);
    markSeenThisSession(sessionKey);
  }, [slug, storeStatus.isClosedButSchedulable]);

  const storeItemsQuery = useQuery({
    queryFn: async () => await getStoreItems.execute({ slug: slug! }),
    queryKey: ['store-items', slug],
    enabled: Boolean(slug),
    staleTime: 0, // Sempre fresh
  });

  const itemsQuery = useQuery({
    queryFn: async () => await listItems.execute(),
    queryKey: ['cart-items'],
    staleTime: 0, // Sempre fresh para carrinho
    gcTime: 5 * 60 * 1000, // 5 minutos
    refetchOnMount: true, // Força refetch ao montar para garantir dados atualizados
  });

  const hasCartItems =
    itemsQuery.data && Array.isArray(itemsQuery.data) && itemsQuery.data.length > 0;

  // Modal de Promoções (issue 316): deriva as ofertas do cardápio completo já
  // carregado (não da lista filtrada pela busca) e o exibe uma única vez por
  // sessão e por loja. Só habilita a abertura quando há promoções válidas — sem
  // ofertas, nada é exibido nem marcado como visto (default seguro).
  const promotionalItems = usePromotionalItems(storeItemsQuery.data?.items);
  const { isOpen: isPromotionsModalOpen, close: handleClosePromotionsModal } =
    usePromotionsModalSession({
      storeKey: slug,
      enabled: promotionalItems.length > 0,
    });

  // Função para filtrar itens baseado no termo de busca
  const filterItems = (items: StoreItemsByCategory, searchTerm: string): StoreItemsByCategory => {
    if (!searchTerm.trim()) {
      return items;
    }

    const filteredItems: StoreItemsByCategory = {};
    const lowerSearchTerm = searchTerm.toLowerCase().trim();

    Object.entries(items).forEach(([category, categoryItems]) => {
      const filteredCategoryItems = categoryItems.filter(item => {
        // Busca por nome
        const nameMatch = item.name.toLowerCase().includes(lowerSearchTerm);

        // Busca por descrição
        const descriptionMatch = item.description?.toLowerCase().includes(lowerSearchTerm) || false;

        // Busca por preço (formato: R$ 21,00 ou 21.00)
        const priceMatch =
          item.price.toLowerCase().includes(lowerSearchTerm) ||
          item.price.replace(',', '.').includes(lowerSearchTerm) ||
          item.price.replace('.', ',').includes(lowerSearchTerm);

        return nameMatch || descriptionMatch || priceMatch;
      });

      if (filteredCategoryItems.length > 0) {
        filteredItems[category] = filteredCategoryItems;
      }
    });

    return filteredItems;
  };

  // Itens filtrados baseados na busca
  const filteredItems = useMemo(() => {
    const items = storeItemsQuery.data?.items || {};
    return filterItems(items, debouncedSearchTerm);
  }, [storeItemsQuery.data?.items, debouncedSearchTerm]);

  // Categorias resolvidas (ordenação dinâmica + categorias virtuais com
  // etiquetas). As categorias dinâmicas (Promoções/Populares/Novidades) agora
  // chegam prontas no metadado do backend; o app apenas as renderiza a partir
  // de `categories`, sem montá-las manualmente no cliente. Faz fallback para a
  // listagem simples quando o backend não envia metadados de categoria.
  const categories = useMemo(() => {
    // Filtro "Combos" (PRAT-79): categoria virtual com todos os combos.
    return withCombosCategory(
      resolveMenuCategories(filteredItems, storeItemsQuery.data?.categories)
    );
  }, [filteredItems, storeItemsQuery.data?.categories]);

  // Promoção por quantidade (PRAT-184): quem participa ganha selo no card
  // ("2º com 50%") e chip no cabeçalho da categoria ("2º item com 50%"), para
  // ficar claro que a promoção é só de "Hambúrguers", não de "Lanches e Combos".
  const quantityPromotion = useMemo(() => {
    const rule = storeItemsQuery.data?.quantity_promotion ?? null;
    if (!isQuantityPromotionActive(rule, QUANTITY_PROMOTION_CHANNEL)) {
      return {
        rule: null,
        itemLabelFor: () => null as string | null,
        categoryLabelFor: () => null as string | null,
      };
    }
    const chip = quantityPromotionChipLabel(rule);
    const headerChip = quantityPromotionLabel(rule);
    return {
      rule,
      /** Selo do card: "2º com 50%" quando o produto participa. */
      itemLabelFor: (item: StoreItem) => (isItemInQuantityPromotion(rule, item) ? chip : null),
      /** Chip do cabeçalho: "2º item com 50%" quando a categoria participa. */
      categoryLabelFor: (category: { items: StoreItem[]; isVirtual?: boolean }) =>
        isCategoryInQuantityPromotion(rule, category) ? headerChip : null,
    };
  }, [storeItemsQuery.data?.quantity_promotion]);

  // PRAT-187: produto de família de atacado mostra o menor preço por faixa como
  // "A partir de" no card.
  const wholesaleFromFor = useCallback(
    (item: StoreItem) => {
      const family = familyForItem(storeItemsQuery.data?.wholesale_pricing ?? null, item.id);
      return family ? lowestTierPrice(family, getDisplayPrice(item).price) : null;
    },
    [storeItemsQuery.data?.wholesale_pricing]
  );

  function handleBack() {
    navigateBackward();
    navigate('/');
  }

  function handleSelectProduct(product: StoreItem) {
    console.log('🟢 Produto selecionado:', {
      productId: product.id,
      productName: product.name,
    });
    setSelectedProductId(product.id);
    setIsProductDrawerOpen(true);
  }

  function handleCloseProductDrawer() {
    console.log('🔴 Fechando drawer de produto');
    setIsProductDrawerOpen(false);
    setSelectedProductId(null);

    // Limpa os parâmetros da URL se existirem
    const urlParams = new URLSearchParams(window.location.search);
    if (
      urlParams.has('editProduct') ||
      urlParams.has('editItemId') ||
      urlParams.has('fromCart') ||
      urlParams.has('clube')
    ) {
      console.log('🧹 Limpando parâmetros da URL');
      navigate(`/${slug}`, { replace: true });
    }
  }

  function handleOpenStoreDetails() {
    setIsStoreDetailsModalOpen(true);
  }

  function handleOpenReferral() {
    if (!slug) return;
    navigate(`/${slug}/indique-e-ganhe`);
  }

  // Vitrine de benefícios (PRAT-110): cupons públicos da loja, Clube e
  // Afili'amados lado a lado, mais a barra de progresso do cupom no rodapé.
  const couponsQuery = useStoreCoupons(slug);
  const coupons = useMemo(() => couponsQuery.data ?? [], [couponsQuery.data]);
  const clubSummaryQuery = useClubSummary(slug!, { enabled: Boolean(slug) });
  const [isCouponsDrawerOpen, setIsCouponsDrawerOpen] = useState(false);
  const [selectedCouponCode, setSelectedCouponCode] = useState<string | null>(() =>
    getPendingCoupon()
  );

  const cartSubtotal = useMemo(
    () =>
      Array.isArray(itemsQuery.data)
        ? itemsQuery.data.reduce((acc, item) => acc + Number(item.total_price || 0), 0)
        : 0,
    [itemsQuery.data]
  );

  const couponProgress = useMemo(
    () =>
      pickCouponProgress(
        coupons,
        cartSubtotal,
        selectedCouponCode,
        Number(storeInfoQuery.data?.delivery_charge) || 0
      ),
    [coupons, cartSubtotal, selectedCouponCode, storeInfoQuery.data?.delivery_charge]
  );

  const highlightItems = useMemo(
    () => pickHighlights(storeItemsQuery.data?.items),
    [storeItemsQuery.data?.items]
  );

  function handleSelectCoupon(coupon: StoreCoupon) {
    const saved = savePendingCoupon(coupon.code);
    if (!saved) return;
    setSelectedCouponCode(saved);
    setIsCouponsDrawerOpen(false);
    enhancedToast.success('Cupom escolhido', {
      description: `O cupom ${saved} entra sozinho no seu pedido.`,
    });
  }

  function handleOpenClub() {
    if (!slug) return;
    navigate(`/${slug}/clube`);
  }

  function handleCloseStoreDetails() {
    setIsStoreDetailsModalOpen(false);
  }

  // Lista de avaliações da loja (PRAT-43).
  const [isReviewsOpen, setIsReviewsOpen] = useState(false);

  function handleOpenReviews() {
    setIsReviewsOpen(true);
  }

  function handleCloseReviews() {
    setIsReviewsOpen(false);
  }

  function handleCloseSchedulingInfo() {
    setIsSchedulingInfoModalOpen(false);
  }

  function handleSearchChange(value: string) {
    setSearchTerm(value);
  }

  // PRAT-19: busca no cardápio. Espera a pessoa parar de digitar para não
  // contar "p", "pi", "piz"... como buscas separadas.
  const trackedSearchTerm = useDebounce(searchTerm, 1200);
  const lastTrackedSearch = useRef('');
  useEffect(() => {
    const term = trackedSearchTerm.trim().toLowerCase();
    if (term.length < 2 || term === lastTrackedSearch.current) return;
    lastTrackedSearch.current = term;
    const results = Object.values(filterItems(storeItemsQuery.data?.items || {}, term)).reduce(
      (acc, list) => acc + list.length,
      0
    );
    track.search(term, results);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só quando o termo assenta
  }, [trackedSearchTerm]);

  // PRAT-19: categoria vista ao rolar ou tocar na aba. A primeira categoria
  // fica ativa sozinha ao abrir a loja, então ela não conta como navegação.
  const seenCategories = useRef<Set<string>>(new Set());
  function trackCategoryView(category: string) {
    if (!category || seenCategories.current.has(category)) return;
    const isFirstActivation = seenCategories.current.size === 0;
    seenCategories.current.add(category);
    if (!isFirstActivation) track.categoryView(category);
  }

  // O tracking de página é feito automaticamente pelo usePageTracking

  return {
    storeInfoQuery,
    storeItemsQuery,
    slug,
    handleBack,
    handleSelectProduct,
    items: filteredItems,
    categories,
    hasCartItems,
    isStoreDetailsModalOpen,
    handleOpenStoreDetails,
    handleCloseStoreDetails,
    isReviewsOpen,
    handleOpenReviews,
    handleCloseReviews,
    storeStatus,
    isSchedulingInfoModalOpen,
    handleCloseSchedulingInfo,
    referralProgram: referralProgramQuery.data,
    handleOpenReferral,
    searchTerm,
    handleSearchChange,
    trackCategoryView,
    isProductDrawerOpen,
    selectedProductId,
    handleCloseProductDrawer,
    promotionalItems,
    isPromotionsModalOpen,
    handleClosePromotionsModal,
    coupons,
    clubSummary: clubSummaryQuery.data,
    couponProgress,
    selectedCouponCode,
    isCouponsDrawerOpen,
    handleOpenCoupons: () => setIsCouponsDrawerOpen(true),
    handleCloseCoupons: () => setIsCouponsDrawerOpen(false),
    handleSelectCoupon,
    handleOpenClub,
    highlightItems,
    quantityPromotion,
    wholesaleFromFor,
  };
}
